import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, TrashSimple, BookBookmark, SlidersHorizontal, TextAa } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { projectApi, miscApi } from "@/api";
import { useProject } from "@/pages/ProjectShell";

const LEVELS = ["Correction", "Fluidité", "Édition", "Littéraire", "Réécriture"];
const LEVEL_DESC = [
  "Orthographe, grammaire, ponctuation.",
  "Corrections + petites améliorations.",
  "Travail éditorial plus approfondi.",
  "Suggestions stylistiques avancées.",
  "Propositions importantes de réécriture.",
];
const CATEGORIES = [
  ["orthographe", "Orthographe"], ["grammaire", "Grammaire"], ["syntaxe", "Syntaxe"],
  ["ponctuation", "Ponctuation"], ["typographie", "Typographie"], ["repetitions", "Répétitions"],
  ["style", "Style"], ["rythme", "Rythme"], ["personnages", "Personnages"],
  ["lieux", "Lieux"], ["chronologie", "Chronologie"], ["coherence", "Cohérence"],
];

export default function Settings() {
  const { pid } = useParams();
  const { project, loadProject } = useProject();
  const [settings, setSettings] = useState(project?.settings || {});
  const [dict, setDict] = useState([]);
  const [newWord, setNewWord] = useState("");
  const [profile, setProfile] = useState(project?.style_profile);

  useEffect(() => { setSettings(project?.settings || {}); setProfile(project?.style_profile); }, [project]);
  useEffect(() => { miscApi.dictionary(pid).then(setDict); }, [pid]);

  const save = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    await projectApi.update(pid, { settings: next });
    loadProject();
  };

  const toggleCat = (cat) => {
    const cats = settings.categories || [];
    save({ categories: cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats, cat] });
  };
  const allCats = () => save({ categories: CATEGORIES.map((c) => c[0]) });

  const addWord = async () => {
    if (!newWord.trim()) return;
    await miscApi.addWord(pid, newWord.trim());
    setNewWord("");
    miscApi.dictionary(pid).then(setDict);
    toast.success("Ajouté au dictionnaire");
  };
  const removeWord = async (id) => { await miscApi.removeWord(pid, id); miscApi.dictionary(pid).then(setDict); };

  const refreshProfile = async () => {
    const p = await projectApi.styleProfile(pid);
    setProfile(p);
    toast.success("Profil stylistique recalculé");
  };

  const level = settings.edit_level || 2;

  return (
    <div className="p-8 max-w-3xl fade-up space-y-10">
      <div>
        <p className="overline text-primary mb-1">Paramètres du projet</p>
        <h1 className="font-serif text-4xl font-light tracking-tight">Réglages</h1>
      </div>

      {/* Intensity */}
      <section className="border border-border bg-surface rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4"><SlidersHorizontal size={18} weight="duotone" className="text-primary" /><h2 className="font-serif text-xl">Intensité éditoriale</h2></div>
        <div className="mb-3 flex items-baseline gap-3">
          <span className="font-serif text-3xl text-primary">Niveau {level}</span>
          <span className="font-medium">{LEVELS[level - 1]}</span>
        </div>
        <Slider min={1} max={5} step={1} value={[level]} onValueChange={(v) => save({ edit_level: v[0] })} className="my-4" data-testid="edit-level-slider" />
        <p className="text-sm text-muted-foreground">{LEVEL_DESC[level - 1]}</p>
      </section>

      {/* Categories */}
      <section className="border border-border bg-surface rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl">Paramètres d'analyse</h2>
          <Button size="sm" variant="outline" onClick={allCats} className="rounded-sm" data-testid="select-all-categories">Tout sélectionner</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {CATEGORIES.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`category-${key}`}>
              <Checkbox checked={(settings.categories || []).includes(key)} onCheckedChange={() => toggleCat(key)} />
              {label}
            </label>
          ))}
        </div>
      </section>

      {/* Style profile */}
      <section className="border border-border bg-surface rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><TextAa size={18} weight="duotone" className="text-primary" /><h2 className="font-serif text-xl">Profil stylistique</h2></div>
          <Button size="sm" variant="outline" onClick={refreshProfile} className="rounded-sm" data-testid="refresh-style">Recalculer</Button>
        </div>
        {profile ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div><p className="overline text-muted-foreground">Longueur moy. phrases</p><p className="font-serif text-2xl">{profile.avg_sentence_length} mots</p></div>
            <div><p className="overline text-muted-foreground">Richesse lexicale</p><p className="font-serif text-2xl">{profile.vocabulary_richness}%</p></div>
            <div><p className="overline text-muted-foreground">Mots uniques</p><p className="font-serif text-2xl">{(profile.unique_words || 0).toLocaleString("fr")}</p></div>
            <div><p className="overline text-muted-foreground">Marqueurs de dialogue</p><p className="font-serif text-2xl">{profile.dialogue_markers}</p></div>
            <div><p className="overline text-muted-foreground">Style narratif</p><p className="font-serif text-2xl">{profile.narrative_style}</p></div>
          </div>
        ) : <p className="text-sm text-muted-foreground">Importez un manuscrit puis recalculez le profil.</p>}
      </section>

      {/* Dictionary */}
      <section className="border border-border bg-surface rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4"><BookBookmark size={18} weight="duotone" className="text-primary" /><h2 className="font-serif text-xl">Dictionnaire personnel — mots à ignorer</h2></div>
        <div className="flex gap-2 mb-4">
          <Input value={newWord} onChange={(e) => setNewWord(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addWord()} placeholder="Nom propre, lieu fictif, terme inventé…" data-testid="dict-input" />
          <Button onClick={addWord} className="rounded-sm gap-1" data-testid="dict-add"><Plus size={16} /> Ajouter</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {dict.length === 0 && <p className="text-sm text-muted-foreground">Aucun mot ignoré.</p>}
          {dict.map((w) => (
            <span key={w.id} className="flex items-center gap-1.5 text-sm px-3 py-1 rounded-sm bg-secondary" data-testid={`dict-word-${w.word}`}>
              {w.word}
              <button onClick={() => removeWord(w.id)} className="text-muted-foreground hover:text-destructive"><TrashSimple size={14} /></button>
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
